import { SpotlightTourStep } from "components/ProductAnnouncement/SpotlightTour";
import {
  ProductAnnouncementPhase,
  useProductAnnouncement,
} from "components/ProductAnnouncement/useProductAnnouncement";
import { ProductAnnouncementKey, SubmissionStepType } from "generated-graphql/graphql";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useScoutAccessPackage } from "routes/Customer/Scout/scoutAccess";

import {
  SCOUT_CITATION_TOUR_STEP,
  toSpotlightSteps,
  WASTE_STEPPER_TOUR_STEPS,
} from "./wasteStepperAnnouncementConfig";

export type UseWasteStepperTourProps = {
  /** Document-form submissions only; the interactive 8700-12 has different steps. */
  isDocumentForm: boolean;
  hasCitations: boolean;
  /** Step types actually rendered on this submission's rail. */
  presentSteps: SubmissionStepType[];
  /** The Ask Scout button lives in the Understand step's body, not on the rail. */
  isOnUnderstandStep: boolean;
  /** Plain callback, not `useStepperNavigation` — this hook's caller isn't a descendant of `WasteStepperProvider`. */
  goToStep: (step: SubmissionStepType, options?: { replace?: boolean }) => void;
};

export type WasteStepperTourState = {
  steps: SpotlightTourStep[] | null;
  phase: ProductAnnouncementPhase;
  dismissModal: () => void;
  softDismissModal: () => void;
  dontShowAgain: boolean;
  setDontShowAgain: (checked: boolean) => void;
  startTour: () => void;
  dismissTour: () => void;
  completeTour: () => void;
  viewTourStep: (step: number) => void;
  /** True once the rail has everything the tour needs to point at. Gates the "show me again" help icon. */
  eligible: boolean;
  /** False once the user has finished with this announcement (completed the tour, or dismissed it for good). Gates the opt-out affordances, which only mean something while it can still show on its own. */
  isPending: boolean;
  /** Reopens the intro modal on demand, bypassing the one-time server gate — for a "show me again" affordance. Goes through the modal rather than straight to the tour so the walkthrough keeps its framing. */
  reopenModal: () => void;
};

/**
 * Owns the one-time walkthrough of the document-form step rail: eligibility,
 * the Scout stop, and the driver.js step list. Called once per stepper page so
 * the tour driver and the "show me again" icon share one surface.
 *
 * Held back until the submission has regulatory citations: without them the
 * Understand step renders no "why this is required" section, so the tour's
 * first stop would point at nothing. Waiting costs nothing — the list-page
 * modal has already delivered the announcement, and this key shows its own
 * intro modal before the tour starts.
 *
 * Scout tenants get one extra stop on the Ask Scout button, so the feature is
 * introduced in the same walkthrough rather than a second overlay of its own.
 * That button only exists on the Understand step, so a Scout tenant who
 * starts the tour elsewhere is sent there first — the tour is one-time, and a
 * signer routed straight to Review & sign would otherwise never be shown the
 * button. Relocation happens on the "start tour" click rather than as soon as
 * the modal is eligible, so landing on the page never silently navigates
 * someone who hasn't engaged with the announcement yet.
 */
export function useWasteStepperTour({
  isDocumentForm,
  hasCitations,
  presentSteps,
  isOnUnderstandStep,
  goToStep,
}: UseWasteStepperTourProps): WasteStepperTourState {
  const { scoutAccessPackage, loading: scoutAccessLoading } = useScoutAccessPackage();

  // Every anchor must be on the rail. A missing one leaves driver.js
  // highlighting a centered dummy element instead of the step.
  const allAnchorsPresent = WASTE_STEPPER_TOUR_STEPS.every(({ step }) =>
    presentSteps.includes(step)
  );
  // Scout gating (permissions, the current user) resolves over the network.
  // Opening before it lands would build the step list without the Scout stop
  // and then rebuild it underneath the user, restarting the tour.
  const eligible = isDocumentForm && hasCitations && allAnchorsPresent && !scoutAccessLoading;

  // Same conditions the Ask Scout button itself renders on (any Scout package).
  const wantsScoutStep = scoutAccessPackage != null;
  const includeScoutStep = wantsScoutStep && isOnUnderstandStep;

  const steps = useMemo(() => {
    const railSteps = toSpotlightSteps(WASTE_STEPPER_TOUR_STEPS);
    if (!includeScoutStep) {
      return railSteps;
    }
    return [...railSteps.slice(0, 1), SCOUT_CITATION_TOUR_STEP, ...railSteps.slice(1)];
  }, [includeScoutStep]);

  const {
    phase,
    shouldShow,
    dismissModal,
    softDismissModal,
    dontShowAgain,
    setDontShowAgain,
    startTour: openTour,
    completeTour,
    dismissTour,
    viewTourStep,
    reopenModal,
  } = useProductAnnouncement(ProductAnnouncementKey.WasteDocumentStepperTour_2026_08, {
    skip: !eligible,
  });

  // The Scout stop's anchor lives in the Understand step's body. If "start
  // tour" is clicked from elsewhere, land on Understand first and open the
  // tour once there, so driver.js has something to point at.
  const pendingStartRef = useRef(false);
  const startTour = useCallback(() => {
    if (wantsScoutStep && !isOnUnderstandStep) {
      pendingStartRef.current = true;
      goToStep(SubmissionStepType.Understand, { replace: true });
      return;
    }
    openTour();
  }, [wantsScoutStep, isOnUnderstandStep, goToStep, openTour]);

  useEffect(() => {
    if (pendingStartRef.current && isOnUnderstandStep) {
      pendingStartRef.current = false;
      openTour();
    }
  }, [isOnUnderstandStep, openTour]);

  // Freeze the step list for the life of one tour session. `includeScoutStep`
  // (and so `steps`) can otherwise flip mid-tour if `isOnUnderstandStep`
  // changes, tearing down and rebuilding driver.js back at step 1.
  const frozenStepsRef = useRef<typeof steps | null>(null);
  if (phase === "tour") {
    frozenStepsRef.current ??= steps;
  } else {
    frozenStepsRef.current = null;
  }

  return {
    steps: frozenStepsRef.current,
    phase,
    isPending: shouldShow,
    dismissModal,
    softDismissModal,
    dontShowAgain,
    setDontShowAgain,
    startTour,
    dismissTour,
    completeTour,
    viewTourStep,
    eligible,
    reopenModal,
  };
}
